import {ReactNode, useMemo} from "react";
import {Box, Typography} from "@mui/material";
import {ArticleImage, createKeywordContext, DropdownSection, RenderContext, renderText} from "./RichText";
import {ArticleBlock, Keyword} from "../Util/types";

type ArticleBodyProps = {
    blocks: ArticleBlock[],
    color: string,
    // Keyword definitions to tag inline throughout the body (including inside
    // accordions - a single shared context so each keyword is tagged once).
    keywords?: Keyword[]
}

// Renders inline <a> links spliced into paragraph text (from scimos_convert.py's
// extract_links) by splitting on the link text and wrapping each match, tagging
// keywords in whatever text is left over.
function renderParagraphText(text: string, ctx: RenderContext, links: {text: string, href: string}[] = []) {
    if (links.length === 0) return renderText(text, ctx);
    const parts: (string | JSX.Element)[] = [text];
    links.forEach((link, i) => {
        const last = parts[parts.length - 1];
        if (typeof last !== "string") return;
        const index = last.indexOf(link.text);
        if (index === -1) return;
        parts[parts.length - 1] = last.slice(0, index);
        parts.push(<a key={i} href={link.href} target="_blank" rel="noreferrer">{link.text}</a>);
        parts.push(last.slice(index + link.text.length));
    });
    return parts.map((part, i) => typeof part === "string" ?
        <span key={i}>{renderText(part, ctx)}</span> : part);
}

function renderBlock(block: ArticleBlock, color: string, key: number, ctx: RenderContext): ReactNode {
    switch (block.type) {
    case "heading":
        return <Typography key={key} variant={block.level === 2 ? "h4" : "h5"} sx={{mt: 3, mb: 1}}>{block.text}</Typography>;
    case "paragraph":
        return <Typography key={key} component={"p"} sx={{my: 1.5}}>{renderParagraphText(block.text, ctx, block.links)}</Typography>;
    case "list":
        return (
            <Box key={key} component={block.ordered ? "ol" : "ul"} sx={{my: 1.5, pl: 3}}>
                {block.items.map((item, i) => <li key={i}><Typography component={"span"}>{renderText(item, ctx)}</Typography></li>)}
            </Box>
        );
    case "accordion":
        return (
            <DropdownSection key={key} title={block.title} color={color}>
                {block.blocks.map((b, i) => renderBlock(b, color, i, ctx))}
            </DropdownSection>
        );
    case "image":
        return block.src ?
            <ArticleImage key={key} src={block.src}/> :
            <Box key={key} sx={{my: 2, py: 4, textAlign: "center", bgcolor: "#f2f2f2", borderRadius: 1, color: "text.secondary"}}>
                <Typography variant={"body2"}>Image not yet available</Typography>
            </Box>;
    default:
        return null;
    }
}

// Renders the block schema tools/scimos_convert.py produces (heading /
// paragraph / list / accordion / image), as stored in the learn, news and
// book-reviews collections.
export default function ArticleBody({blocks, color, keywords = []}: ArticleBodyProps) {
    const ctx = useMemo(() => createKeywordContext(keywords, color), [keywords, color]);
    return <>{blocks.map((block, index) => renderBlock(block, color, index, ctx))}</>;
}
